<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useUserStore } from '@/authentication/user.store'

const userStore = useUserStore()
const accountsBaseUrl = import.meta.env.VITE_ACCOUNTS_URL
const signOutUrl = `${accountsBaseUrl}/users/sign_out`

const now = ref(Date.now())
let ticker: ReturnType<typeof setInterval> | undefined

onMounted(() => {
  ticker = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})

onUnmounted(() => {
  if (ticker) {
    clearInterval(ticker)
  }
})

const secondsRemaining = computed(() => {
  const expiresAt = userStore.impersonationExpiresAt
  if (!expiresAt) {
    return 0
  }
  const millisRemaining = new Date(expiresAt).getTime() - now.value
  return Math.max(0, Math.floor(millisRemaining / 1000))
})

const countdown = computed(() => {
  const minutes = Math.floor(secondsRemaining.value / 60)
  const seconds = secondsRemaining.value % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
})
</script>

<template>
  <div
    v-if="userStore.isImpersonating"
    role="region"
    aria-label="Impersonation session"
    class="flex w-full flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-orange-50 px-4 py-2 text-center text-md leading-snug text-base-defaultText"
  >
    <span>
      Viewing as <span class="fs-exclude font-bold">{{ userStore.userName }}</span>
    </span>
    <span aria-hidden="true">&mdash;</span>
    <span>
      <span class="sr-only">Session expires in </span>
      <span>{{ countdown }}</span>
    </span>
    <span aria-hidden="true">&mdash;</span>
    <a
      :href="signOutUrl"
      class="cursor-pointer bg-transparent p-0 font-bold underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-base-defaultText"
    >
      Exit
    </a>
  </div>
</template>
